import Cookies from "js-cookie";

export interface StoredTrackingParams {
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  utm_term: string | null;
  utm_content: string | null;
  _fbp: string | null;
  _fbc: string | null;
  // Google click ids, the counterpart to _fbp/_fbc. Capture all three: iOS
  // carries gbraid (app) or wbraid (web) INSTEAD of gclid post-ATT, so gclid
  // alone is blind to a large share of YouTube traffic.
  gclid: string | null;
  gbraid: string | null;
  wbraid: string | null;
  // Aggregate campaign ids, also appended by Google Ads auto-tagging.
  // gad_campaignid names the campaign for auto-tagged traffic, which arrives
  // with a click id but no UTMs.
  gad_campaignid: string | null;
  gad_source: string | null;
}

/**
 * Google click ids, in precedence order.
 *
 * These are MUTUALLY EXCLUSIVE: Google requires exactly one of gclid, gbraid
 * or wbraid per conversion ("You must provide exactly one of the following
 * identifiers"). They are treated as a single slot rather than three
 * independent fields — see resolveClickId.
 *
 * gclid first because it is the most common and processes fastest (<12h vs up
 * to 72h for the braid ids); wbraid before gbraid because this is a web
 * funnel, so the iOS *web* id is the one that can actually apply.
 */
export const GOOGLE_CLICK_ID_KEYS = ["gclid", "wbraid", "gbraid"] as const;

/**
 * Aggregate campaign ids. Not mutually exclusive and not click identifiers —
 * these merge normally.
 */
export const GOOGLE_CAMPAIGN_KEYS = ["gad_campaignid", "gad_source"] as const;

export const GOOGLE_TRACKING_PARAM_KEYS = [
  ...GOOGLE_CLICK_ID_KEYS,
  ...GOOGLE_CAMPAIGN_KEYS,
] as const;

type ClickIdKey = (typeof GOOGLE_CLICK_ID_KEYS)[number];
type ClickIdSlot = Record<ClickIdKey, string | null>;

const EMPTY_CLICK_IDS: ClickIdSlot = {
  gclid: null,
  wbraid: null,
  gbraid: null,
};

/**
 * Collapses the three click ids to at most one, last touch wins.
 *
 * A visitor can accumulate more than one over a 30-day cookie window — e.g. a
 * Search click (gclid) on Monday and a YouTube click on an iPhone (wbraid) on
 * Friday. Forwarding both would upload a conversion carrying two click ids,
 * which Google does not accept. The Data Manager schema does not reject it
 * outright either (they are flat optional fields, not a union), so it would
 * fail quietly rather than loudly.
 *
 * An incoming click id always displaces the stored one, so the most recent ad
 * interaction is the one credited.
 */
export function resolveClickId(
  incoming: Partial<Record<ClickIdKey, string | null>>,
  stored: Partial<Record<ClickIdKey, string | null>>
): ClickIdSlot {
  const fresh = GOOGLE_CLICK_ID_KEYS.find((key) => incoming[key]);
  if (fresh) {
    return { ...EMPTY_CLICK_IDS, [fresh]: incoming[fresh] as string };
  }
  const kept = GOOGLE_CLICK_ID_KEYS.find((key) => stored[key]);
  if (kept) {
    return { ...EMPTY_CLICK_IDS, [kept]: stored[kept] as string };
  }
  return { ...EMPTY_CLICK_IDS };
}

const TRACKING_PARAMS_STORAGE_KEY = "utmParams";

export function buildFbcFromFbclid(
  fbclid: string,
  creationTime = Date.now()
): string {
  return `fb.2.${creationTime}.${fbclid}`;
}

export function getFbcCreationTime(fbc: string | null | undefined): number | null {
  if (!fbc) return null;

  const match = fbc.match(/^fb\.\d+\.(\d+)\..+$/);
  if (!match) return null;

  const creationTime = Number(match[1]);
  return Number.isFinite(creationTime) ? creationTime : null;
}

function getNewerFbc(
  ...fbcCandidates: Array<string | null | undefined>
): string | null {
  return fbcCandidates.reduce<string | null>((newerFbc, candidateFbc) => {
    if (!candidateFbc) return newerFbc;
    if (!newerFbc) return candidateFbc;

    const candidateCreationTime = getFbcCreationTime(candidateFbc);
    const newerCreationTime = getFbcCreationTime(newerFbc);

    if (candidateCreationTime === null && newerCreationTime !== null) {
      return newerFbc;
    }

    if (candidateCreationTime !== null && newerCreationTime === null) {
      return candidateFbc;
    }

    if (candidateCreationTime === null || newerCreationTime === null) {
      return candidateFbc;
    }

    return candidateCreationTime >= newerCreationTime ? candidateFbc : newerFbc;
  }, null);
}

export function getStoredTrackingParams(
  storage: Pick<Storage, "getItem"> = localStorage
): Partial<StoredTrackingParams> {
  try {
    const stored = storage.getItem(TRACKING_PARAMS_STORAGE_KEY);
    if (!stored) return {};
    return JSON.parse(stored) as Partial<StoredTrackingParams>;
  } catch {
    return {};
  }
}

export function getCapturedTrackingParams(
  search: string,
  options: {
    now?: () => number;
    storage?: Pick<Storage, "getItem">;
  } = {}
): StoredTrackingParams | null {
  const urlParams = new URLSearchParams(search);
  const fbclid = urlParams.get("fbclid");
  const incomingGoogleParams = Object.fromEntries(
    GOOGLE_TRACKING_PARAM_KEYS.map((key) => [key, urlParams.get(key)])
  ) as Record<(typeof GOOGLE_TRACKING_PARAM_KEYS)[number], string | null>;

  const incomingParams = {
    utm_source: urlParams.get("utm_source"),
    utm_medium: urlParams.get("utm_medium"),
    utm_campaign: urlParams.get("utm_campaign"),
    utm_term: urlParams.get("utm_term"),
    utm_content: urlParams.get("utm_content"),
    _fbp: urlParams.get("_fbp") ?? urlParams.get("fbp"),
    _fbc: urlParams.get("_fbc") ?? urlParams.get("fbc"),
    ...incomingGoogleParams,
  };

  if (!fbclid && !Object.values(incomingParams).some(Boolean)) {
    return null;
  }

  const stored = getStoredTrackingParams(options.storage);
  const fbclidFbc = fbclid
    ? buildFbcFromFbclid(fbclid, options.now ? options.now() : Date.now())
    : null;
  const fbc = getNewerFbc(stored._fbc, incomingParams._fbc, fbclidFbc);

  // Click ids collapse to at most one; campaign ids merge normally.
  const clickIds = resolveClickId(incomingParams, stored);
  const campaignParams = Object.fromEntries(
    GOOGLE_CAMPAIGN_KEYS.map((key) => [
      key,
      incomingParams[key] ?? stored[key] ?? null,
    ])
  ) as Record<(typeof GOOGLE_CAMPAIGN_KEYS)[number], string | null>;

  return {
    utm_source: incomingParams.utm_source ?? stored.utm_source ?? null,
    utm_medium: incomingParams.utm_medium ?? stored.utm_medium ?? null,
    utm_campaign: incomingParams.utm_campaign ?? stored.utm_campaign ?? null,
    utm_term: incomingParams.utm_term ?? stored.utm_term ?? null,
    utm_content: incomingParams.utm_content ?? stored.utm_content ?? null,
    _fbp: incomingParams._fbp ?? stored._fbp ?? null,
    _fbc: fbc,
    ...clickIds,
    ...campaignParams,
  };
}

export function persistTrackingParams(
  params: StoredTrackingParams,
  domain: string
): void {
  localStorage.setItem(TRACKING_PARAMS_STORAGE_KEY, JSON.stringify(params));

  Object.entries(params).forEach(([key, value]) => {
    if (value) {
      Cookies.set(key, value, {
        expires: 30,
        sameSite: "lax",
        domain,
      });
    } else if ((GOOGLE_CLICK_ID_KEYS as readonly string[]).includes(key)) {
      // Actively clear a displaced click id. Cookies are otherwise only ever
      // written, so a stale gclid would outlive the wbraid that replaced it
      // and both would be read back at checkout.
      Cookies.remove(key, { domain });
    }
  });
}
